import React, { useState, useEffect } from "react";
import { FaPencilAlt } from "react-icons/fa";
import axios from "axios";
import { format, parseISO } from "date-fns";

const Shipping = ({ caseId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shippingData, setShippingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOption, setSelectedOption] = useState("");
  const [isOtherDateSelected, setIsOtherDateSelected] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedShipper, setSelectedShipper] = useState("");
  const [trackNumber, setTrackNumber] = useState("");

  // Fetch shipping data
  const fetchShippingData = async () => {
    try {
      const response = await axios.get(
        `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}`
      );

      setShippingData(response.data);
      setSelectedShipper(response.data.shipper || "No Shipper");
      setTrackNumber(response.data.shipmentTrackingUrls?.[0] || ""); // Get first tracking URL
      setLoading(false);
    } catch (error) {
      console.error(
        "❌ Error fetching shipping data:",
        error.response?.data || error.message
      );
      setError("Failed to fetch shipping data. Please try again.");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (caseId) {
      fetchShippingData();
    }
  }, [caseId]);

  const handleOptionChange = (e) => {
    const option = e.target.value;
    setSelectedOption(option);
    setIsOtherDateSelected(option === "Other Date");
    if (option !== "Other Date") {
      setSelectedDate("");
    }
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleShipperChange = (e) => {
    setSelectedShipper(e.target.value);
  };

  const handleTrackNumberChange = (e) => {
    setTrackNumber(e.target.value);
  };

  const handleConfirm = async () => {
    try {
      const updateData = {};
      if (selectedOption === "Ship Today") {
        updateData.delivery_date = new Date().toISOString();
      } else if (selectedOption === "Ship Next Business Day") {
        const nextDay = new Date();
        nextDay.setDate(nextDay.getDate() + 1);
        updateData.delivery_date = nextDay.toISOString();
      } else if (selectedOption === "Other Date" && selectedDate) {
        const parsedDate = new Date(selectedDate);
        if (!isNaN(parsedDate)) {
          updateData.delivery_date = parsedDate.toISOString();
        } else {
          alert("Please select a valid date");
          return;
        }
      }
      if (selectedShipper && selectedShipper !== "No Shipper") {
        updateData.shipper = selectedShipper;
      }
      if (trackNumber) {
        updateData.trackNumber = trackNumber;
      }

      await axios.put(
        `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}/shipping`,
        updateData
      );

      await fetchShippingData();
      alert("Shipping details updated successfully");
      closeModal();
    } catch (error) {
      console.error(
        "❌ Error updating shipping details:",
        error.response?.data || error.message
      );
      alert(
        "Error updating shipping details: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    setSelectedShipper(shippingData?.shipper || "No Shipper");
    setTrackNumber(shippingData?.shipmentTrackingUrls?.[0] || "");
    setSelectedOption("Keep Current"); // Match select option
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOption("");
    setIsOtherDateSelected(false);
    setSelectedDate("");
    setSelectedShipper(shippingData?.shipper || "No Shipper");
    setTrackNumber(shippingData?.shipmentTrackingUrls?.[0] || "");
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  // Format delivery date
  const deliveryDate =
    shippingData?.delivery_date && !isNaN(new Date(shippingData.delivery_date))
      ? format(parseISO(shippingData.delivery_date), "dd MMM yyyy")
      : "Not Set";

  // Format delivered date and time
  const deliveredDateTime =
    shippingData?.delivered_date &&
    !isNaN(new Date(shippingData.delivered_date))
      ? format(parseISO(shippingData.delivered_date), "dd MMM yyyy - HH:mm")
      : "Not Delivered";

  const shipper = shippingData?.shipper || "No Shipper";
  const trackNumberDisplay =
    shippingData?.shipmentTrackingUrls?.[0] || "Not Set";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center" }}>
        <h3>Shipping</h3>
        <button
          onClick={openModal}
          style={{
            cursor: "pointer",
            backgroundColor: "#004aad",
            color: "white",
            padding: "5px 8px",
            borderRadius: "4px",
            alignItems: "center",
            justifyContent: "center",
            borderColor: "#004aad",
            height: "30px",
            width: "40px",
            marginLeft: "10px",
          }}
        >
          <FaPencilAlt style={{ fontSize: "15px", marginBottom: "20px" }} />
        </button>
      </div>
      <div>
        <p style={{ fontWeight: "bold", fontSize: "16px" }}>
          Ship To Account Date:
        </p>
        <h6>{deliveryDate}</h6>
      </div>
      <div>
        <p style={{ fontWeight: "bold", fontSize: "16px" }}>Shipper:</p>
        <h6>{shipper}</h6>
      </div>
      <div>
        <p style={{ fontWeight: "bold", fontSize: "16px" }}>Delivery Date:</p>
        <h6>{deliveryDate}</h6>
      </div>
      <div>
        <p style={{ fontWeight: "bold", fontSize: "16px" }}>
          Delivered Date and Time:
        </p>
        <h6
          style={{
            color: deliveredDateTime === "Not Delivered" ? "#666" : "black",
          }}
        >
          {deliveredDateTime}
        </h6>
      </div>
      <div>
        <p style={{ fontWeight: "bold", fontSize: "16px" }}>Track #:</p>
        <h6>{trackNumberDisplay}</h6>
      </div>

      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "8px",
              minWidth: "600px",
              textAlign: "left",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
              border: "1px solid #ccc",
            }}
          >
            <div
              style={{
                backgroundColor: "#004aad",
                padding: "10px",
                borderBottom: "1px solid #ccc",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderRadius: "6px",
              }}
            >
              <h2 style={{ margin: 0, color: "white" }}>Shipping</h2>
              <button
                onClick={closeModal}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "22px",
                  color: "white",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: "15px" }}>
              <h4 style={{ color: "#305f9cff", margin: "10px 0" }}>
                Shipping Details
              </h4>
              <div
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  borderTop: "3px solid #004aad",
                }}
              >
                <div>
                  <div style={{ marginBottom: "10px" }}>
                    <label>Reschedule?</label>
                    <select
                      style={{
                        width: "100%",
                        padding: "5px",
                        marginTop: "5px",
                      }}
                      onChange={handleOptionChange}
                      value={selectedOption}
                    >
                      <option>Keep Current</option>
                      <option>Ship Today</option>
                      <option>Ship Next Business Day</option>
                      <option>Other Date</option>
                    </select>
                  </div>

                  {isOtherDateSelected && (
                    <div style={{ marginBottom: "10px" }}>
                      <label>
                        Other Date <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        style={{
                          width: "100%",
                          padding: "5px",
                          marginTop: "5px",
                        }}
                        min={new Date().toISOString().split("T")[0]}
                      />
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: "10px" }}>
                  <label>Shipper *</label>
                  <select
                    style={{ width: "100%", padding: "5px", marginTop: "5px" }}
                    value={selectedShipper}
                    onChange={handleShipperChange}
                  >
                    <option>No Shipper</option>
                    <option>Local driver</option>
                    <option>Royal Mail</option>
                    <option>OTHER- UPS COURIER</option>
                  </select>
                  <p style={{ fontSize: "12px", color: "#666" }}>
                    Same Day. Last delivery by end of business day
                  </p>
                  <p style={{ fontSize: "12px", color: "#666" }}>
                    Delivery Date:{" "}
                    {selectedOption === "Ship Today"
                      ? format(new Date(), "dd MMM yyyy")
                      : selectedOption === "Ship Next Business Day"
                      ? format(
                          new Date(
                            new Date().setDate(new Date().getDate() + 1)
                          ),
                          "dd MMM yyyy"
                        )
                      : selectedDate
                      ? format(parseISO(selectedDate), "dd MMM yyyy")
                      : deliveryDate}
                  </p>
                </div>
                <div style={{ marginBottom: "10px" }}>
                  <label>Track #</label>
                  <input
                    type="text"
                    value={trackNumber}
                    onChange={handleTrackNumberChange}
                    style={{ width: "100%", padding: "5px", marginTop: "5px" }}
                  />
                </div>
                <div style={{ marginBottom: "10px" }}>
                  <label>Shipping cost</label>
                  <input
                    type="text"
                    style={{ width: "100%", padding: "5px", marginTop: "5px" }}
                  />
                </div>

                {/* Display delivered date and time in modal */}
                <div
                  style={{
                    marginBottom: "10px",
                    padding: "10px",
                    backgroundColor:
                      deliveredDateTime === "Not Delivered"
                        ? "#f8f9fa"
                        : "#e8f5e8",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                  }}
                >
                  <p
                    style={{
                      fontWeight: "bold",
                      margin: "0 0 5px 0",
                      color:
                        deliveredDateTime === "Not Delivered"
                          ? "#6c757d"
                          : "black",
                    }}
                  >
                    Current Delivery Status:
                  </p>
                  <p
                    style={{
                      margin: 0,
                      color:
                        deliveredDateTime === "Not Delivered"
                          ? "#6c757d"
                          : "black",
                      fontWeight:
                        deliveredDateTime === "Not Delivered"
                          ? "normal"
                          : "bold",
                    }}
                  >
                    {deliveredDateTime}
                  </p>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "20px",
                }}
              >
                <button
                  onClick={closeModal}
                  style={{
                    backgroundColor: "#004aad",
                    border: "none",
                    padding: "8px 15px",
                    marginRight: "10px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    color: "white",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  style={{
                    backgroundColor: "#004aad",
                    border: "none",
                    padding: "8px 15px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    color: "white",
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shipping;
